/** In-house payment plan rules (aligned with cc-managed new-enrollment / payment-plan-form). */

/** Minimum basket / course total (pence, incl. VAT) for in-house payment plans. */
export const PAYMENT_PLAN_MIN_FULL_TOTAL_PENCE = 300_000;

/** Contentful entry after which the in-house payment plan card is inserted on course pages. */
export const IN_HOUSE_PAYMENT_PLAN_CARD_ANCHOR_ENTRY_ID =
  "5xc36VXkG9UvSH4EvvAPzd";

/**
 * Page-scoped Payment Options grids that keep their own CMS intro when the
 * course is under the £3,000 in-house plan threshold (instead of the shared
 * below-threshold rewrite).
 */
export const PAYMENT_OPTIONS_CUSTOM_INTRO_ENTRY_IDS = [
  "L4PmuPaymentOptions001",
  "L5TatPaymentOptions001",
  "L4HifuPaymentOptions001",
  "L4BladePaymentOptions001",
  "L4PeelPaymentOptions001",
  "L4RfuPaymentOptions001",
  "L4DermPaymentOptions001",
  "L4PlasmaPaymentOptions001",
  "L4IqaPaymentOptions001",
  "L5RfPaymentOptions001",
  "L5ChemPeelPaymentOptions001",
  "L6MesoPaymentOptions001",
  "L3AetPaymentOptions001",
  "L3CavaPaymentOptions001",
  "L3BlsPaymentOptions001",
] as const;

/**
 * Page-scoped Payment Options grids that should also trigger the in-house
 * calculator (same placement as the shared fees box).
 */
export const IN_HOUSE_PAYMENT_PLAN_CARD_ANCHOR_ENTRY_IDS = [
  IN_HOUSE_PAYMENT_PLAN_CARD_ANCHOR_ENTRY_ID,
  "L4SkinPaymentOptions001",
  "L5SkinPaymentOptions001",
] as const;

/** Shared "In House Payment Plans" contentPair inside the course Payment Options fees box. */
export const IN_HOUSE_PAYMENT_PLAN_FEES_BOX_ITEM_ID = "27E0j4PNtHgmqhr8a081fa";

/** Marketing card visibility threshold (course total incl. VAT). */
export const IN_HOUSE_PAYMENT_PLAN_CARD_MIN_INC_VAT_PENCE =
  PAYMENT_PLAN_MIN_FULL_TOTAL_PENCE;

export function paymentPlanMinimumLabel() {
  return "£3,000 (including VAT)";
}

/**
 * Intro copy for the shared Payment Options fees box when the course total is
 * below the in-house plan threshold. Keeps Klarna / Snap; drops 0% / 30% /
 * monthly-payment promises that only apply from £3,000.
 */
export function paymentOptionsFeesBoxIntroBelowThreshold() {
  return (
    "Start today with a deposit, pay in full, or spread the cost with Klarna or Snap Finance. " +
    "Payment plans are available when your total spend is £3,000 or more, across any courses. " +
    "[Finance options](/finance/) at checkout. Secure your place on the dates listed on this page.\n\n" +
    "Book Your Training Spot Today or Request a Callback for More Information"
  );
}

export const UK_VAT_RATE = 0.2;

type CourseEventPricing = {
  events?: Array<{
    registrations?: unknown[];
    seats?: number;
    seat_price?: number;
    price?: number;
  }>;
  standardPrice?: number;
};

export function getCourseListingPricePence(
  event?: CourseEventPricing | null,
): number | null {
  if (!event) {
    return null;
  }

  const nextAvailable = event.events?.find(
    (instance) => (instance.registrations?.length ?? 0) < (instance.seats ?? 0),
  );

  if (typeof nextAvailable?.seat_price === "number") {
    return nextAvailable.seat_price;
  }

  if (typeof nextAvailable?.price === "number") {
    return nextAvailable.price;
  }

  if (typeof event.standardPrice === "number") {
    return event.standardPrice;
  }

  return null;
}

/** Matches checkout VAT calculation (`Math.round(20% ex-VAT) + ex-VAT`). */
export function addVatPence(amountExVatPence: number): number {
  const vat = Math.round(UK_VAT_RATE * amountExVatPence);
  return Math.round(amountExVatPence + vat);
}

export function formatCoursePriceIncVatFromExVatPence(
  exVatPence: number | null | undefined,
): string | null {
  if (exVatPence == null || !Number.isFinite(exVatPence)) {
    return null;
  }

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(addVatPence(Math.round(exVatPence)) / 100);
}

type CourseSeatPriceDetail = {
  wasPrice?: number | null;
  standardPrice?: number | null;
  package?: boolean | null;
};

/**
 * Inc-VAT seat price for course date cards, fees box, and hero pills.
 * Only surfaces a compare/was price for package was_price or when seat ex-VAT
 * is strictly below standard ex-VAT (real sale), never for stale was_price alone.
 */
export function resolveCourseSeatPriceComparison(
  eventDetail: CourseSeatPriceDetail | null | undefined,
  seatPriceExVatPence: number | null | undefined,
) {
  const seatExVat = Math.round(Number(seatPriceExVatPence || 0));
  const standardExVat = Math.round(Number(eventDetail?.standardPrice || 0));
  const wasExVat = Math.round(Number(eventDetail?.wasPrice || 0));

  const hasSeatDiscount =
    seatExVat > 0 && standardExVat > 0 && seatExVat < standardExVat;

  const showCompare =
    Boolean(eventDetail?.package && wasExVat > 0) || hasSeatDiscount;

  const compareExVat =
    eventDetail?.package && wasExVat > 0
      ? wasExVat
      : hasSeatDiscount
        ? standardExVat
        : 0;

  const currentIncVatPence = seatExVat > 0 ? addVatPence(seatExVat) : 0;
  const compareIncVatPence =
    compareExVat > 0 ? addVatPence(compareExVat) : 0;

  return {
    currentIncVatPence,
    compareIncVatPence,
    showCompare,
  };
}

export function getCourseListingPriceIncVatPence(
  event?: CourseEventPricing | null,
): number | null {
  const priceExVatPence = getCourseListingPricePence(event);
  if (priceExVatPence === null) {
    return null;
  }

  return addVatPence(priceExVatPence);
}

export function shouldShowInHousePaymentPlanCard(
  event?: CourseEventPricing | null,
): boolean {
  const priceIncVatPence = getCourseListingPriceIncVatPence(event);
  return (
    priceIncVatPence !== null &&
    priceIncVatPence >= IN_HOUSE_PAYMENT_PLAN_CARD_MIN_INC_VAT_PENCE
  );
}

export function paymentPlanCoursesFromEventDetail(
  event?: {
    id?: string;
    events?: Array<{ detail_id?: string }>;
  } | null,
): Array<{ event: { id: string; detail_id: string } }> {
  if (!event?.id) {
    return [];
  }

  return [
    {
      event: {
        id: event.id,
        detail_id: event.events?.[0]?.detail_id ?? event.id,
      },
    },
  ];
}

export const PAYMENT_PLAN_DEPOSIT_RATE = 0.3;

export const PAYMENT_PLAN_REMAINDER_RATE = 0.7;

/**
 * Booking deposit floor (inc. VAT pence).
 * A place is secured with 10% of the training total or this amount, whichever is greater.
 */
export const BOOKING_DEPOSIT_MIN_PENCE = 25_000;

export function calculateBookingDeposit(fullTotalPence: number): number {
  const tenPercent = Math.round(fullTotalPence * 0.1);
  return Math.max(tenPercent, BOOKING_DEPOSIT_MIN_PENCE);
}

/** Flat admin fee added when a learner chooses an in-house payment plan (inc. VAT pence). */
export const PAYMENT_PLAN_ADMIN_FEE_PENCE = 25_000;

export const PAYMENT_PLAN_ADMIN_FEE_GBP = PAYMENT_PLAN_ADMIN_FEE_PENCE / 100;

/** Pathway To Aesthetics package (event_detail). */
export const PATHWAY_TO_AESTHETICS_EVENT_DETAIL_ID =
  "349205c1-dd47-4083-a983-d51ede4233d2";

/** Course detail IDs eligible for 9-month plans (cc-managed payment-plan-form). */
export const PAYMENT_PLAN_ELIGIBLE_9_MONTH_DETAIL_IDS = [
  "ce783b41-8556-4e95-b025-a4238b59c131",
  PATHWAY_TO_AESTHETICS_EVENT_DETAIL_ID,
  "e0eb0262-e08a-4ae0-a96b-3915d43f1485",
  "dacff515-c3f2-443d-9d38-52e7c525e13a",
  "3eac27ac-57d4-4e5d-b50b-e5531c416288",
  "42307561-5856-4cae-8bdf-d2299ffd77de",
  "66ec4723-2799-4f0a-9fe0-a00b43f7b13f",
] as const;

export type PaymentPlanDetail = {
  monthlyAmount: number;
  numberOfPayments: number;
  totalPlanAmount: number;
  amountAfterDeposit: number;
  difference: number;
  depositAmount: number;
  courseDepositAmount: number;
  adminFeeAmount: number;
  totalWithAdminFee: number;
  createdAt: string;
};

export function courseDetailIdsFromCheckoutCourses(
  courses: Array<{ event?: { id?: string; detail_id?: string } }> = [],
): string[] {
  return courses
    .map((course) => course?.event?.detail_id ?? course?.event?.id)
    .filter((id): id is string => Boolean(id));
}

export function isEligibleFor9MonthPaymentPlan(
  courses: Array<{ event?: { id?: string; detail_id?: string } }> = [],
): boolean {
  const ids = courseDetailIdsFromCheckoutCourses(courses);
  return ids.some((id) =>
    (PAYMENT_PLAN_ELIGIBLE_9_MONTH_DETAIL_IDS as readonly string[]).includes(id),
  );
}

export function getPaymentPlanPeriodOptions(
  courses: Array<{ event?: { id?: string; detail_id?: string } }> = [],
): number[] {
  return isEligibleFor9MonthPaymentPlan(courses) ? [3, 6, 9] : [3, 6];
}

export function isPaymentPlanEligible(
  fullTotalPence: number,
  options: {
    usesAcceleratorStripe?: boolean;
    hasFullPaymentOnlyCourse?: boolean;
    hasBasketDiscount?: boolean;
    lockedOnsiteCpdPackage?: boolean;
  } = {},
): boolean {
  if (fullTotalPence < PAYMENT_PLAN_MIN_FULL_TOTAL_PENCE) {
    return false;
  }
  if (options.usesAcceleratorStripe) {
    return false;
  }
  if (options.hasFullPaymentOnlyCourse) {
    return false;
  }
  if (options.hasBasketDiscount) {
    return false;
  }
  if (options.lockedOnsiteCpdPackage) {
    return false;
  }
  return true;
}

/** 30% of training total, plus the £250 payment-plan admin fee. */
export function calculatePaymentPlanCourseDeposit(
  fullTotalPence: number,
): number {
  return Math.round(fullTotalPence * PAYMENT_PLAN_DEPOSIT_RATE);
}

export function calculatePaymentPlanDeposit(fullTotalPence: number): number {
  return (
    calculatePaymentPlanCourseDeposit(fullTotalPence) +
    PAYMENT_PLAN_ADMIN_FEE_PENCE
  );
}

export function buildPaymentPlanDetail({
  fullTotalPence,
  numberOfPayments,
}: {
  fullTotalPence: number;
  numberOfPayments: number;
}): PaymentPlanDetail {
  const courseDepositAmount = calculatePaymentPlanCourseDeposit(fullTotalPence);
  const adminFeeAmount = PAYMENT_PLAN_ADMIN_FEE_PENCE;
  const depositAmount = courseDepositAmount + adminFeeAmount;
  const amountAfterDeposit = Math.round(fullTotalPence - courseDepositAmount);
  const totalWithAdminFee = Math.round(fullTotalPence + adminFeeAmount);
  const monthlyAmount =
    numberOfPayments > 0
      ? Math.ceil(amountAfterDeposit / numberOfPayments)
      : 0;
  const totalFromInstalments = monthlyAmount * numberOfPayments;

  return {
    monthlyAmount,
    numberOfPayments,
    totalPlanAmount: amountAfterDeposit,
    amountAfterDeposit,
    difference: totalFromInstalments - amountAfterDeposit,
    depositAmount,
    courseDepositAmount,
    adminFeeAmount,
    totalWithAdminFee,
    createdAt: new Date().toISOString(),
  };
}
